import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import './PhoneInput.css';

type Country = {
  iso: string;
  name: string;
  dial: string;
  mask: string;
};

const COUNTRIES: Country[] = [
  { iso: 'RU', name: 'Россия', dial: '7', mask: '(###) ###-##-##' },
  { iso: 'KZ', name: 'Казахстан', dial: '7', mask: '(###) ###-##-##' },
  { iso: 'BY', name: 'Беларусь', dial: '375', mask: '(##) ###-##-##' },
  { iso: 'AM', name: 'Армения', dial: '374', mask: '## ######' },
  { iso: 'AZ', name: 'Азербайджан', dial: '994', mask: '## ### ## ##' },
  { iso: 'GB', name: 'Великобритания', dial: '44', mask: '#### ######' },
  { iso: 'DE', name: 'Германия', dial: '49', mask: '### #######' },
  { iso: 'GE', name: 'Грузия', dial: '995', mask: '### ## ## ##' },
  { iso: 'IL', name: 'Израиль', dial: '972', mask: '##-###-####' },
  { iso: 'IN', name: 'Индия', dial: '91', mask: '##### #####' },
  { iso: 'CY', name: 'Кипр', dial: '357', mask: '## ######' },
  { iso: 'CN', name: 'Китай', dial: '86', mask: '### #### ####' },
  { iso: 'KG', name: 'Кыргызстан', dial: '996', mask: '### ### ###' },
  { iso: 'NL', name: 'Нидерланды', dial: '31', mask: '## #### ####' },
  { iso: 'AE', name: 'ОАЭ', dial: '971', mask: '## ### ####' },
  { iso: 'SG', name: 'Сингапур', dial: '65', mask: '#### ####' },
  { iso: 'US', name: 'США', dial: '1', mask: '(###) ###-####' },
  { iso: 'TR', name: 'Турция', dial: '90', mask: '(###) ### ## ##' },
  { iso: 'UZ', name: 'Узбекистан', dial: '998', mask: '## ### ## ##' },
  { iso: 'FR', name: 'Франция', dial: '33', mask: '# ## ## ## ##' },
  { iso: 'CH', name: 'Швейцария', dial: '41', mask: '## ### ## ##' },
];

const DEFAULT_ISO = 'RU';

function nationalLength(mask: string): number {
  return mask.split('').filter((char) => char === '#').length;
}

function FlagIcon({ iso }: { iso: string }) {
  return (
    <img
      className="dcc-phone__flag"
      src={`https://flagcdn.com/24x18/${iso.toLowerCase()}.png`}
      alt=""
      width={20}
      height={15}
    />
  );
}

function formatNational(digits: string, mask: string): string {
  let index = 0;
  let out = '';
  for (const char of mask) {
    if (index >= digits.length) break;
    if (char === '#') {
      out += digits[index];
      index += 1;
    } else {
      out += char;
    }
  }
  return out;
}

function countryByIso(iso: string): Country {
  return COUNTRIES.find((country) => country.iso === iso) ?? COUNTRIES[0];
}

function parsePhone(value: string): { iso: string; digits: string } {
  const all = value.replace(/\D/g, '');
  if (!all) return { iso: DEFAULT_ISO, digits: '' };

  if (all.length === 11 && all.startsWith('8')) {
    return { iso: 'RU', digits: all.slice(1) };
  }

  const byDial = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const country of byDial) {
    if (!all.startsWith(country.dial)) continue;
    const national = all.slice(country.dial.length);
    const limit = nationalLength(country.mask);
    if (national.length === 0 || national.length > limit) continue;
    if (country.dial === '7') return { iso: 'RU', digits: national };
    if (country.dial === '1') return { iso: 'US', digits: national };
    return { iso: country.iso, digits: national };
  }

  return { iso: DEFAULT_ISO, digits: all.slice(0, nationalLength(countryByIso(DEFAULT_ISO).mask)) };
}

function composePhone(country: Country, digits: string): string {
  if (!digits) return '';
  return `+${country.dial} ${formatNational(digits, country.mask)}`;
}

export function isCompleteInternationalPhone(value: string): boolean {
  const parsed = parsePhone(value);
  if (!parsed.digits) return false;
  return parsed.digits.length === nationalLength(countryByIso(parsed.iso).mask);
}

function digitsFromRaw(raw: string, country: Country): string {
  let digits = raw.replace(/\D/g, '');
  const limit = nationalLength(country.mask);
  if (country.iso === 'RU' && digits.length === 11 && digits.startsWith('8')) {
    digits = digits.slice(1);
  }
  if (digits.startsWith(country.dial) && digits.length > limit) {
    digits = digits.slice(country.dial.length);
  }
  return digits.slice(0, limit);
}

type Props = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

export default function PhoneInput({ id, value, onChange, disabled = false }: Props) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const emittedRef = useRef(value);
  const initial = parsePhone(value);
  const [iso, setIso] = useState(initial.iso);
  const [digits, setDigits] = useState(initial.digits);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const country = countryByIso(iso);
  const formatted = formatNational(digits, country.mask);
  const placeholder = country.mask.replace(/#/g, '0');

  useEffect(() => {
    if (value === emittedRef.current) return;
    const parsed = parsePhone(value);
    setIso(parsed.iso);
    setDigits(parsed.digits);
    emittedRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    searchRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const emit = (nextIso: string, nextDigits: string) => {
    const next = composePhone(countryByIso(nextIso), nextDigits);
    emittedRef.current = next;
    onChange(next);
  };

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return COUNTRIES;
    return COUNTRIES.filter((item) => {
      const haystack = `${item.name} ${item.iso} +${item.dial}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [query]);

  return (
    <div className={`dcc-phone${open ? ' dcc-phone--open' : ''}${disabled ? ' dcc-phone--disabled' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="dcc-phone__country"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => {
          setOpen((current) => !current);
          setQuery('');
        }}
      >
        <FlagIcon iso={country.iso} />
        <span>+{country.dial}</span>
      </button>
      <input
        id={id}
        className="dcc-phone__input"
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        placeholder={placeholder}
        value={formatted}
        disabled={disabled}
        aria-label={`Номер телефона, ${country.name}`}
        onChange={(event) => {
          const raw = event.target.value;
          let nextDigits = digitsFromRaw(raw, country);
          if (nextDigits === digits && raw.length < formatted.length) {
            nextDigits = digits.slice(0, -1);
          }
          setDigits(nextDigits);
          emit(iso, nextDigits);
        }}
      />
      {open && (
        <div className="dcc-phone__menu">
          <input
            ref={searchRef}
            className="dcc-phone__search"
            value={query}
            placeholder="Страна или код"
            aria-label="Найти страну"
            onChange={(event) => setQuery(event.target.value)}
          />
          <ul id={listId} className="dcc-phone__list" role="listbox" aria-label="Страна">
            {filtered.length === 0 ? (
              <li className="dcc-phone__empty">Ничего не найдено</li>
            ) : filtered.map((item) => (
              <li key={item.iso}>
                <button
                  type="button"
                  role="option"
                  aria-selected={item.iso === iso}
                  className={`dcc-phone__option${item.iso === iso ? ' dcc-phone__option--selected' : ''}`}
                  onClick={() => {
                    const limit = nationalLength(item.mask);
                    const nextDigits = digits.slice(0, limit);
                    setIso(item.iso);
                    setDigits(nextDigits);
                    emit(item.iso, nextDigits);
                    setOpen(false);
                  }}
                >
                  <FlagIcon iso={item.iso} />
                  <span className="dcc-phone__option-name">{item.name}</span>
                  <span className="dcc-phone__option-dial">+{item.dial}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
